/**
 * Base64 Encoder/Decoder View
 * Interactive Base64 encoding and decoding
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService } from '../../../services/utils.service.js';

type Mode = 'encode' | 'decode';

export const Base64View: React.FC = () => {
    const [input, setInput] = useState('');
    const [mode, setMode] = useState<Mode>('encode');
    const [urlSafe, setUrlSafe] = useState(false);
    const [result, setResult] = useState('');
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState<'input' | 'mode' | 'urlsafe' | 'convert'>('input');
    const [copied, setCopied] = useState(false);

    const service = new UtilsService();

    useInput((input, key) => {
        if (key.tab) {
            const fields: typeof focusedField[] = ['input', 'mode', 'urlsafe', 'convert'];
            const currentIndex = fields.indexOf(focusedField);
            setFocusedField(fields[(currentIndex + 1) % fields.length]);
        } else if (key.return) {
            if (focusedField === 'mode') {
                setMode(mode === 'encode' ? 'decode' : 'encode');
                setResult('');
                setError('');
            } else if (focusedField === 'urlsafe') {
                setUrlSafe(!urlSafe);
            } else {
                // Convert on Enter from input or convert button
                handleConvert();
            }
        } else if (input === 'c' && result) {
            handleCopy();
        }
    });

    const handleConvert = () => {
        if (!input) {
            setError('Input cannot be empty');
            return;
        }

        try {
            setError('');
            if (mode === 'encode') {
                const encoded = service.base64Encode(input, urlSafe);
                setResult(encoded);
            } else {
                const decoded = service.base64Decode(input);
                setResult(decoded.toString('utf-8'));
            }
            setCopied(false);
        } catch (err) {
            setError(`Failed to ${mode}: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setResult('');
        }
    };

    const handleCopy = async () => {
        try {
            const { default: clipboardy } = await import('clipboardy');
            await clipboardy.write(result);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    📝 Base64 Encoder/Decoder
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'input' ? 'green' : undefined}>
                            {focusedField === 'input' ? '▶ ' : '  '}Input text:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={60}>
                        {focusedField === 'input' ? (
                            <TextInput value={input} onChange={setInput} placeholder="Enter text..." />
                        ) : (
                            <Text dimColor={!input}>{input || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'mode' ? 'green' : undefined}>
                            {focusedField === 'mode' ? '▶ ' : '  '}Mode:
                        </Text>
                    </Box>
                    <Text bold color={focusedField === 'mode' ? 'yellow' : undefined}>
                        {mode === 'encode' ? 'ENCODE' : 'DECODE'}
                    </Text>
                    {focusedField === 'mode' && (
                        <Text dimColor> (Enter to toggle)</Text>
                    )}
                </Box>

                {mode === 'encode' && (
                    <Box marginBottom={1}>
                        <Text color={focusedField === 'urlsafe' ? 'green' : undefined}>
                            {focusedField === 'urlsafe' ? '▶ ' : '  '}
                            [{urlSafe ? '✓' : ' '}] URL-Safe (+ → -, / → _)
                        </Text>
                    </Box>
                )}

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'convert' ? 'green' : undefined}
                        color={focusedField === 'convert' ? 'black' : 'green'}
                    >
                        {focusedField === 'convert' ? '▶ ' : '  '}
                        [ {mode === 'encode' ? 'Encode' : 'Decode'} ]
                    </Text>
                </Box>
            </Box>

            {error && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="red"
                    paddingX={2}
                    paddingY={1}
                    marginBottom={1}
                >
                    <Text color="red">✗ Error: {error}</Text>
                </Box>
            )}

            {result && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Result:
                        </Text>
                        {copied && (
                            <Box marginLeft={2}>
                                <Text color="green">✓ Copied to clipboard!</Text>
                            </Box>
                        )}
                    </Box>

                    <Box flexDirection="column">
                        <Text dimColor>Mode: {mode.toUpperCase()}</Text>
                        <Box marginTop={1}>
                            <Text wrap="wrap">{result}</Text>
                        </Box>
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | Enter: Toggle/Convert | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
